import _ from 'lodash';
import { logout } from '../legacy-components/Security/securityReducer';

const BASE_BACKEND_URL = process.env.REACT_APP_API;
const BASE_REPORTING_URL = process.env.REACT_APP_REPORT;
const urlForPasswordReset = BASE_BACKEND_URL + 'forgottenpassword/resetpassword';

const queryParamsFromObject = (parameterObject) => {
  if (!parameterObject || _.isEmpty(parameterObject)) {
    return '';
  }
  const params = [];
  for (const [key, value] of Object.entries(parameterObject)) {
    const encodedValue = encodeURIComponent(value);
    params.push(`${key}=${encodedValue}`);
  }

  return `?${params.join('&')}`;
};

const fetch = async (...args) => {
  return window.fetch(...args).then(async (resp) => {
    const pathname = new URL(window.location.href).pathname.toLowerCase();
    // If token has expired; force re-auth.
    if (resp.status === 401) {
      logout();
      const q = new URLSearchParams();
      q.append('authError', 'Session expired, please log in again.');
      if (!pathname.startsWith('/login')) {
        window.location.assign('/login?' + q.toString());
      }
      return;
    }
    // If user is forbidden; redirect away from page.
    if (resp.status === 403) {
      const q = new URLSearchParams();
      q.append('authError', "Sorry, you don't have permission to do that.");
      if (!pathname.startsWith('/main')) {
        window.location.assign('/main?' + q.toString());
      }
      return;
    }

    if (resp.status === 400 && resp.url === urlForPasswordReset) {
      return Promise.resolve(resp);
    }

    // If validation error; return the response so the caller can handle it.
    if (resp.status === 422) {
      return Promise.resolve(resp);
    }

    if (resp.ok) {
      return resp;
    } else {
      const error = await resp.text();
      return Promise.reject(new Error(error));
    }
  });
};

const apiFetch = async (url, options = {}, authorised) => {
  const token = sessionStorage.getItem('token');
  const headers = {
    ...options.headers,
    ...(authorised && { Authorization: token ? `${token}` : undefined }),
  };

  const response = await fetch(url, { ...options, headers });

  // If the response is an error, throw an error to be caught by the caller.
  // If the response is a validation error (422), return the response to be handled by the caller.
  if (response.status >= 400 && response.status !== 422) {
    throw new Error('Network request failed');
  } else {
    return options.onResponse ? await options.onResponse(response) : await response.json();
  }
};

/**
 * @typedef {Object} FetchParameters
 * @property {string} url - The endpoint URL
 * @property {object} [options] - Fetch options (headers, body, etc.)
 * @property {object} [params] - Query parameters in key value pairs
 * @property {('GET'|'POST'|'PUT'|'DELETE')} [method] - HTTP method to use, if blank will be a GET request
 * @property {boolean} [authorised=true] - Does this request require login, defaults to true
 */

/**
 * Generic API fetch wrapper for get requests to the backend
 * @param {FetchParameters} fetchInput - Object with url, options, params and the HTTP method to use
 * @returns {Promise<any>} - Resolves with JSON or throws an error
 */
export const backendFetch = ({ url, options = {}, params = {}, method, authorised = true, isReporting = false }) => {
  const fullOptions = method ? { ...options, method } : options;
  const baseUrl = isReporting ? BASE_REPORTING_URL : BASE_BACKEND_URL;

  return apiFetch(baseUrl + url + queryParamsFromObject(params), fullOptions, authorised);
};
